import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Tabs,
  Tab,
  TablePagination,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Paper,
  Divider,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Tooltip
} from '@mui/material';
import {
  Business as BusinessIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  LocalOffer as OfferIcon,
  Tune as TuneIcon,

  ExpandMore as ExpandMoreIcon,
  Upload as UploadIcon,
  Link as LinkIcon,
  Settings as SettingsIcon,
  Search as SearchIcon,
  Category as CategoryIcon,
  Label as LabelIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import DashboardLayout from '../components/DashboardLayout';

import categoryService from '../services/categoryService';
import OfferPageBuilderSimple from '../components/OfferPageBuilderSimple';
import { useAuth } from '../context/AuthContext';

const SetUps = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0); // Start with Offer Setup tab active



  // Category Management states
  const [categoriesForManagement, setCategoriesForManagement] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryPage, setCategoryPage] = useState(0);
  const [categoryRowsPerPage, setCategoryRowsPerPage] = useState(10);
  const [categoryTotalCount, setCategoryTotalCount] = useState(0);
  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  
  // Category dialog states
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [openSubcategoryDialog, setOpenSubcategoryDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [selectedCategoryForSub, setSelectedCategoryForSub] = useState(null);

  // Category form states
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    icon: '',
    color: '#3B82F6',
    isActive: true,
    sortOrder: 0
  });

  const [subcategoryForm, setSubcategoryForm] = useState({
    name: '',
    description: '',
    isActive: true,
    sortOrder: 0
  });

  // General states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const offerBuilderRef = useRef();

  // Load categories on component mount
  useEffect(() => {
    if (activeTab === 0) { // Category Management tab
      fetchCategoriesForManagement();
    }
  }, [activeTab]);

  // Fetch categories for management with pagination
  const fetchCategoriesForManagement = async () => {
    try {
      setCategoryLoading(true);
      const params = {
        page: categoryPage + 1,
        limit: categoryRowsPerPage,
        search: categorySearchTerm
      };
      const data = await categoryService.getAllCategories(params);
      setCategoriesForManagement(data.categories);
      setCategoryTotalCount(data.total);
    } catch (error) {
      setError('Failed to fetch categories');
    } finally {
      setCategoryLoading(false);
    }
  };

  // Fetch categories for offer dropdowns
  const fetchCategories = async () => {
    try {
      await categoryService.getCategoriesForSelect();
      // This function is called to refresh categories used in offer dropdowns
      // The actual categories are fetched when needed by other components
    } catch (error) {
      console.error('Error fetching categories for dropdowns:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 0) { // Category Management tab
      fetchCategoriesForManagement();
    }
  }, [categoryPage, categoryRowsPerPage, categorySearchTerm]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Category Management Functions
  const showSnackbar = (message, severity = 'success') => {
    if (severity === 'success') {
      setSuccess(message);
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(message);
      setTimeout(() => setError(''), 3000);
    }
  };

  const resetCategoryForm = () => {
    setCategoryForm({
      name: '',
      description: '',
      icon: '',
      color: '#3B82F6',
      isActive: true,
      sortOrder: 0
    });
    setEditingCategory(null);
  };

  const resetSubcategoryForm = () => {
    setSubcategoryForm({
      name: '',
      description: '',
      isActive: true,
      sortOrder: 0
    });
    setEditingSubcategory(null);
    setSelectedCategoryForSub(null);
  };

  // Category handlers
  const handleCreateCategory = () => {
    resetCategoryForm();
    setOpenCategoryDialog(true);
  };

  const handleEditCategory = (category) => {
    setCategoryForm({
      name: category.name,
      description: category.description || '',
      icon: category.icon || '',
      color: category.color || '#3B82F6',
      isActive: category.isActive,
      sortOrder: category.sortOrder || 0
    });
    setEditingCategory(category);
    setOpenCategoryDialog(true);
  };

  const handleSaveCategory = async () => {
    try {
      if (editingCategory) {
        await categoryService.updateCategory(editingCategory._id, categoryForm);
        showSnackbar('Category updated successfully');
      } else {
        await categoryService.createCategory(categoryForm);
        showSnackbar('Category created successfully');
      }
      setOpenCategoryDialog(false);
      resetCategoryForm();
      fetchCategoriesForManagement();
      fetchCategories(); // Also refresh categories for offer dropdowns
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to save category', 'error');
    }
  };

  const handleDeleteCategory = async (category) => {
    if (window.confirm(`Are you sure you want to delete "${category.name}"? This will also delete all subcategories.`)) {
      try {
        await categoryService.deleteCategory(category._id);
        showSnackbar('Category deleted successfully');
        fetchCategoriesForManagement();
        fetchCategories(); // Also refresh categories for offer dropdowns
      } catch (error) {
        showSnackbar(error.response?.data?.message || 'Failed to delete category', 'error');
      }
    }
  };

  // Subcategory handlers
  const handleCreateSubcategory = (category) => {
    resetSubcategoryForm();
    setSelectedCategoryForSub(category);
    setOpenSubcategoryDialog(true);
  };

  const handleEditSubcategory = (category, subcategory) => {
    setSubcategoryForm({
      name: subcategory.name,
      description: subcategory.description || '',
      isActive: subcategory.isActive,
      sortOrder: subcategory.sortOrder || 0
    });
    setEditingSubcategory(subcategory);
    setSelectedCategoryForSub(category);
    setOpenSubcategoryDialog(true);
  };

  const handleSaveSubcategory = async () => {
    try {
      if (editingSubcategory) {
        await categoryService.updateSubcategory(
          selectedCategoryForSub._id,
          editingSubcategory._id,
          subcategoryForm
        );
        showSnackbar('Subcategory updated successfully');
      } else {
        await categoryService.addSubcategory(selectedCategoryForSub._id, subcategoryForm);
        showSnackbar('Subcategory created successfully');
      }
      setOpenSubcategoryDialog(false);
      resetSubcategoryForm();
      fetchCategoriesForManagement();
      fetchCategories(); // Also refresh categories for offer dropdowns
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to save subcategory', 'error');
    }
  };

  const handleDeleteSubcategory = async (category, subcategory) => {
    if (window.confirm(`Are you sure you want to delete "${subcategory.name}"?`)) {
      try {
        await categoryService.deleteSubcategory(category._id, subcategory._id);
        showSnackbar('Subcategory deleted successfully');
        fetchCategoriesForManagement();
        fetchCategories(); // Also refresh categories for offer dropdowns
      } catch (error) {
        showSnackbar(error.response?.data?.message || 'Failed to delete subcategory', 'error');
      }
    }
  };

  // Import Template Handler
  const handleImportTemplate = (event) => {
    console.log('🔄 Import button clicked, file selected:', event.target.files[0]);
    const file = event.target.files[0];
    if (!file) {
      console.log('❌ No file selected');
      return;
    }
    console.log('📁 File details:', { 
      name: file.name, 
      size: file.size, 
      type: file.type,
      lastModified: file.lastModified
    });
    
    const reader = new FileReader();
    
    reader.onerror = (error) => {
      console.error('❌ FileReader error:', error);
      alert('Error reading file: ' + error.message);
    };
    
    reader.onload = (e) => {
      try {
        console.log('📖 File read successfully, raw content length:', e.target.result.length);
        console.log('📖 First 100 chars:', e.target.result.substring(0, 100));
        console.log('📖 Last 100 chars:', e.target.result.substring(e.target.result.length - 100));
        
        const json = JSON.parse(e.target.result);
        console.log('✅ JSON parsed successfully:', json);
        console.log('📊 JSON structure:', {
          hasName: !!json.name,
          hasElements: !!json.elements,
          elementsLength: json.elements ? json.elements.length : 0,
          hasPageSettings: !!json.pageSettings
        });
        
        console.log('🔍 Checking offerBuilderRef:', {
          current: offerBuilderRef.current,
          hasLoadMethod: offerBuilderRef.current && offerBuilderRef.current.loadTemplateFromImport
        });
        
        if (offerBuilderRef.current && offerBuilderRef.current.loadTemplateFromImport) {
          console.log('🚀 Calling loadTemplateFromImport...');
          offerBuilderRef.current.loadTemplateFromImport(json);
        } else {
          console.error('❌ Offer builder not ready or import method missing');
          alert('Offer builder not ready or import method missing.');
        }
      } catch (err) {
        console.error('❌ JSON parsing error:', err);
        console.error('❌ Error stack:', err.stack);
        alert('Invalid JSON file: ' + err.message);
      }
    };
    
    console.log('📖 Starting to read file...');
    reader.readAsText(file);
  };

  const renderOfferSetup = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Create Offer Element Template
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Create and manage offer templates, basic elements, and layouts for your campaigns
      </Typography>
      <Button
        variant="outlined"
        component="label"
        sx={{ mb: 2 }}
      >
        Import Template
        <input
          type="file"
          accept=".json,application/json"
          hidden
          onChange={handleImportTemplate}
          onClick={(e) => {
            console.log('🖱️ File input clicked, resetting value');
            e.target.value = '';
          }}
        />
      </Button>
      <OfferPageBuilderSimple ref={offerBuilderRef} templatesOnly={false} customElementsOnly={false} hideCustomElements={true} />
    </Box>
  );

  const renderCategoryManagement = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Set Categories/Problems
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage categories and subcategories for offer organization
      </Typography>

      {/* Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder="Search categories..."
            value={categorySearchTerm}
            onChange={(e) => setCategorySearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {/* Add Category Button */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateCategory}
        >
          Add Category
        </Button>
      </Box>

      {/* Categories List */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        {categoryLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          categoriesForManagement.map((category) => (
            <Accordion key={category._id}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel-${category._id}-content`}
                id={`panel-${category._id}-header`}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <CategoryIcon sx={{ mr: 2, color: category.color }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6">{category.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {category.description || 'No description'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
                    <Chip
                      label={category.isActive ? 'Active' : 'Inactive'}
                      color={category.isActive ? 'success' : 'default'}
                      size="small"
                    />
                    <Chip
                      label={`${category.subcategoryCount} subcategories`}
                      variant="outlined"
                      size="small"
                    />
                    <Tooltip title="Edit Category">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditCategory(category);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Category">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCategory(category);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Subcategories</Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => handleCreateSubcategory(category)}
                    >
                      Add Subcategory
                    </Button>
                  </Box>
                  
                  {category.subcategories && category.subcategories.length > 0 ? (
                    <Grid container spacing={2}>
                      {category.subcategories.map((subcategory) => (
                        <Grid item xs={12} sm={6} md={4} key={subcategory._id}>
                          <Card variant="outlined">
                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <LabelIcon sx={{ mr: 1, fontSize: 16 }} />
                                <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                                  {subcategory.name}
                                </Typography>
                                <Chip
                                  icon={subcategory.isActive ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                  label={subcategory.isActive ? 'Active' : 'Inactive'}
                                  color={subcategory.isActive ? 'success' : 'default'}
                                  size="small"
                                />
                              </Box>
                              {subcategory.description && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  {subcategory.description}
                                </Typography>
                              )}
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                <Tooltip title="Edit Subcategory">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditSubcategory(category, subcategory)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete Subcategory">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteSubcategory(category, subcategory)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      No subcategories found. Click "Add Subcategory" to create one.
                    </Typography>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))
        )}

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={categoryTotalCount}
          rowsPerPage={categoryRowsPerPage}
          page={categoryPage}
          onPageChange={(event, newPage) => setCategoryPage(newPage)}
          onRowsPerPageChange={(event) => {
            setCategoryRowsPerPage(parseInt(event.target.value, 10));
            setCategoryPage(0);
          }}
        />
      </Paper>
    </Box>
  );

  const tabContent = [
    renderCategoryManagement,
    renderOfferSetup
  ];

  return (
    <DashboardLayout title="Set Ups">
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Paper sx={{ mb: 4 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Set Categories/Problems" />
            <Tab label="Create Offer Element Template" />
          </Tabs>
        </Paper>

        <Box sx={{ py: 3 }}>
          {tabContent[activeTab]()}
        </Box>

      </Container>

      {/* Category Dialog */}
      <Dialog open={openCategoryDialog} onClose={() => setOpenCategoryDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCategory ? 'Edit Category' : 'Add New Category'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Category Name"
              fullWidth
              variant="outlined"
              value={categoryForm.name}
              onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Description"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={categoryForm.description}
              onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Icon (Material-UI icon name)"
              fullWidth
              variant="outlined"
              value={categoryForm.icon}
              onChange={(e) => setCategoryForm({ ...categoryForm, icon: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Color"
              type="color"
              fullWidth
              variant="outlined"
              value={categoryForm.color}
              onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Sort Order"
              type="number"
              fullWidth
              variant="outlined"
              value={categoryForm.sortOrder}
              onChange={(e) => setCategoryForm({ ...categoryForm, sortOrder: parseInt(e.target.value) || 0 })}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={categoryForm.isActive}
                  onChange={(e) => setCategoryForm({ ...categoryForm, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCategoryDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveCategory} variant="contained">
            {editingCategory ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Subcategory Dialog */}
      <Dialog open={openSubcategoryDialog} onClose={() => setOpenSubcategoryDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSubcategory ? 'Edit Subcategory' : 'Add New Subcategory'}
          {selectedCategoryForSub && (
            <Typography variant="body2" color="text.secondary">
              Category: {selectedCategoryForSub.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Subcategory Name"
              fullWidth
              variant="outlined"
              value={subcategoryForm.name}
              onChange={(e) => setSubcategoryForm({ ...subcategoryForm, name: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Description"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={subcategoryForm.description}
              onChange={(e) => setSubcategoryForm({ ...subcategoryForm, description: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Sort Order"
              type="number"
              fullWidth
              variant="outlined"
              value={subcategoryForm.sortOrder}
              onChange={(e) => setSubcategoryForm({ ...subcategoryForm, sortOrder: parseInt(e.target.value) || 0 })}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={subcategoryForm.isActive}
                  onChange={(e) => setSubcategoryForm({ ...subcategoryForm, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSubcategoryDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveSubcategory} variant="contained">
            {editingSubcategory ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbars */}
      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccess('')} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={3000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default SetUps; 